// src/pages/Privacy.js
import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import AnimatedSocialLinks from '../components/AnimatedSocialLinks';
import './Home.css';

export default function Privacy() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 relative overflow-x-hidden">
      {/* ───── ANIMATED BACKGROUND BLOBS ───── */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-yellow-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-2000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-pink-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-4000"></div>
      </div>

      {/* ───── SEO ───── */}
      <Helmet>
        <title>Privacy Policy - SErraND Kenya</title>
        <meta
          name="description"
          content="Understand SErraND's Privacy Policy, detailing how we collect, use, and protect your personal information on our Kenyan service provider platform."
        />
        <meta
          name="keywords"
          content="SErraND, privacy policy, Kenya, service providers, data protection, personal information"
        />
        <meta name="author" content="Marvin Useki" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://www.serrand.org/privacy/" />
        <meta property="og:title" content="Privacy Policy - SErraND Kenya" />
        <meta
          property="og:description"
          content="SErraND's Privacy Policy explains how we safeguard your data on our platform for service providers in Kenya."
        />
        <meta property="og:url" content="https://www.serrand.org/privacy/" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://www.serrand.org/images/privacy-image.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Privacy Policy - SErraND Kenya" />
        <meta name="twitter:description" content="SErraND's Privacy Policy for users in Kenya." />
        <meta name="twitter:image" content="https://www.serrand.org/images/privacy-image.jpg" />
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: 'SErraND Privacy Policy',
            url: 'https://www.serrand.org/privacy/',
            description:
              'Learn how SErraND protects your privacy and handles your personal information on our platform for service providers in Kenya.',
            publisher: {
              '@type': 'Organization',
              name: 'SErraND',
              url: 'https://www.serrand.org',
              logo: 'https://www.serrand.org/images/logo.png',
            },
          })}
        </script>
      </Helmet>

      {/* ───── NAVIGATION (Home.js style, no auth) ───── */}
      <nav className="relative z-40 bg-gray-900 bg-opacity-80 backdrop-blur-lg border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center space-x-2 group">
              <div className="logo-container bg-gradient-to-r from-blue-500 to-purple-600 bg-clip-text text-transparent group-hover:from-blue-400 group-hover:to-purple-500 transition-all">
                <span className="text-2xl font-bold">SE</span>
                <span className="text-2xl font-bold">RRA</span>
                <span className="text-2xl font-bold">ND</span>
              </div>
            </Link>

            <div className="hidden md:flex md:items-center md:space-x-6">
              <Link to="/" className="text-gray-300 hover:text-white px-3 py-2 rounded-md text-sm font-medium transition-colors hover:bg-gray-800">Home</Link>
              <Link to="/profile" className="text-gray-300 hover:text-white px-3 py-2 rounded-md text-sm font-medium transition-colors hover:bg-gray-800">Profile</Link>
              <Link to="/contact" className="text-gray-300 hover:text-white px-3 py-2 rounded-md text-sm font-medium transition-colors hover:bg-gray-800">Contact</Link>
              <Link to="/search" className="text-gray-300 hover:text-white px-3 py-2 rounded-md text-sm font-medium transition-colors hover:bg-gray-800">Search Providers</Link>
              <Link to="/terms" className="text-gray-300 hover:text-white px-3 py-2 rounded-md text-sm font-medium transition-colors hover:bg-gray-800">Terms</Link>
              <Link to="/privacy" className="text-gray-300 hover:text-white px-3 py-2 rounded-md text-sm font-medium transition-colors hover:bg-gray-800">Privacy</Link>
            </div>

            <button
              className="md:hidden relative w-8 h-8 flex flex-col justify-center items-center"
              onClick={() => document.getElementById('mobile-menu')?.classList.toggle('hidden')}
              aria-label="Toggle menu"
            >
              <span className="w-6 h-0.5 bg-white transition-all duration-300"></span>
              <span className="w-6 h-0.5 bg-white transition-all duration-300 mt-1"></span>
              <span className="w-6 h-0.5 bg-white transition-all duration-300 mt-1"></span>
            </button>
          </div>
        </div>

        <div id="mobile-menu" className="md:hidden hidden bg-gray-900 bg-opacity-95 backdrop-blur-lg border-t border-gray-700">
          <div className="px-4 py-4 space-y-2">
            <Link to="/" className="block text-gray-300 hover:text-white px-3 py-2 rounded-md text-base font-medium transition-colors hover:bg-gray-800">Home</Link>
            <Link to="/profile" className="block text-gray-300 hover:text-white px-3 py-2 rounded-md text-base font-medium transition-colors hover:bg-gray-800">Profile</Link>
            <Link to="/contact" className="block text-gray-300 hover:text-white px-3 py-2 rounded-md text-base font-medium transition-colors hover:bg-gray-800">Contact</Link>
            <Link to="/search" className="block text-gray-300 hover:text-white px-3 py-2 rounded-md text-base font-medium transition-colors hover:bg-gray-800">Search Providers</Link>
            <Link to="/terms" className="block text-gray-300 hover:text-white px-3 py-2 rounded-md text-base font-medium transition-colors hover:bg-gray-800">Terms</Link>
            <Link to="/privacy" className="block text-gray-300 hover:text-white px-3 py-2 rounded-md text-base font-medium transition-colors hover:bg-gray-800">Privacy</Link>
          </div>
        </div>
      </nav>

      {/* ───── PRIVACY POLICY CONTENT - 100% YOURS, UNCHANGED ───── */}
      <section className="relative py-20 px-4 max-w-6xl mx-auto z-10">
        <div className="text-center mb-12">
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-4">SErraND Privacy Policy</h1>
          <p className="text-xl text-gray-300">Last Updated: May 31, 2025</p>
        </div>

        <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl border border-gray-700 p-8 md:p-12 shadow-2xl backdrop-blur-lg max-w-5xl mx-auto space-y-8 text-gray-300 leading-relaxed">
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">1. Introduction</h2>
            <p>
              At SErraND, we are committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your personal information when you use our platform, SErraND.org. By accessing or using SErraND, you consent to the practices described in this policy. If you do not agree, please do not use our platform.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">2. Information We Collect</h2>
            <p>We collect the following types of information to provide and improve our services:</p>

            <h3 className="text-xl font-semibold text-white mt-6 mb-2">2.1 Personal Identification Information</h3>
            <p>
              When you register, create a profile, or contact us, we may collect your name, email address, phone number, location, and other contact details.
            </p>

            <h3 className="text-xl font-semibold text-white mt-6 mb-2">2.2 Profile Information</h3>
            <p>
              Service providers may share content, such as business names, service descriptions, and photos, to promote their services. This information is publicly visible to facilitate connections.
            </p>

            <h3 className="text-xl font-semibold text-white mt-6 mb-2">2.3 Transaction Information</h3>
            <p>
              We collect details about transactions conducted through the platform, including service details, payment amounts, and our 10% commission.
            </p>

            <h3 className="text-xl font-semibold text-white mt-6 mb-2">2.4 Usage Data</h3>
            <p>
              We collect data about your interactions with the platform, including IP address, browser type, device information, pages visited, and timestamps.
            </p>

            <h3 className="text-xl font-semibold text-white mt-6 mb-2">2.5 Communications</h3>
            <p>
              We collect the content of your communications with us, such as feedback forms, emails, or support inquiries. You consent to receive SMS, email, or other communications as part of our services.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">3. How We Use Your Information</h2>
            <p>We use your information to:</p>
            <ul className="list-disc list-inside mt-4 space-y-2 ml-4">
              <li>Operate and enhance SErraND’s features, connecting clients with service providers.</li>
              <li>Process transactions and manage commissions.</li>
              <li>Personalize your experience with relevant content and recommendations.</li>
              <li>Communicate account updates, respond to inquiries, and send promotional messages.</li>
              <li>Enable promoted placements for service providers.</li>
              <li>Ensure platform security and enforce our Terms of Service.</li>
              <li>Analyze usage trends to improve our services.</li>
              <li>Comply with Kenyan legal requirements.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">4. How We Share Your Information</h2>
            <p>We may share your information as follows:</p>

            <h3 className="text-xl font-semibold text-white mt-6 mb-2">4.1 With Other Users</h3>
            <p>
              Profile and service information you provide may be visible to other users to facilitate connections.
            </p>

            <h3 className="text-xl font-semibold text-white mt-6 mb-2">4.2 Service Providers</h3>
            <p>
              We share information with third-party vendors (e.g., hosting, payment processors) who assist in operating the platform.
            </p>

            <h3 className="text-xl font-semibold text-white mt-6 mb-2">4.3 Legal Requirements</h3>
            <p>
              We may disclose information to comply with Kenyan law or respond to legal requests, such as court orders.
            </p>

            <h3 className="text-xl font-semibold text-white mt-6 mb-2">4.4 Business Transfers</h3>
            <p>
              Your information may be transferred during a merger, acquisition, or sale of SErraND’s assets.
            </p>

            <h3 className="text-xl font-semibold text-white mt-6 mb-2">4.5 With Your Consent</h3>
            <p>
              We may share information with your explicit permission.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">5. Data Security</h2>
            <p>
              We implement industry-standard security measures, such as encryption and access controls, to protect your information. However, no system is completely secure, and we cannot guarantee absolute protection.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">6. Data Retention</h2>
            <p>
              We retain your information for as long as needed to provide services, comply with legal obligations, resolve disputes, or enforce agreements. You may request deletion of your data, subject to legal exceptions.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">7. Your Rights</h2>
            <p>Under Kenyan law, you may have the following rights:</p>
            <ul className="list-disc list-inside mt-4 space-y-2 ml-4">
              <li><strong>Access</strong>: Request a copy of your personal information.</li>
              <li><strong>Correction</strong>: Update inaccurate or incomplete data.</li>
              <li><strong>Deletion</strong>: Request removal of your data, where applicable.</li>
              <li><strong>Objection</strong>: Object to certain data processing activities.</li>
            </ul>
            <p className="mt-4">
              To exercise these rights, contact <a href="mailto:serrand.support@serrand.org" className="text-blue-400 hover:underline">serrand.support@serrand.org</a>.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">8. Third-Party Links</h2>
            <p>
              SErraND may link to third-party websites. We are not responsible for their privacy practices. Please review their policies before sharing information.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">9. Children’s Privacy</h2>
            <p>
              SErraND is not intended for users under 18. We do not knowingly collect data from children. If we discover such data, we will delete it promptly.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">10. Changes to This Privacy Policy</h2>
            <p>
              We may update this Privacy Policy to reflect changes in our practices or legal requirements. We will notify you via website notices or email. Continued use of the platform constitutes acceptance of the updated policy.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">11. Governing Law and Jurisdiction</h2>
            <p>
              This Privacy Policy is governed by Kenyan law. Disputes shall be resolved exclusively in Kenyan courts.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">12. Contact Information</h2>
            <p>
              For questions about this Privacy Policy, contact us at <a href="mailto:serrand.support@serrand.org" className="text-blue-400 hover:underline">serrand.support@serrand.org</a>.
            </p>
          </section>
        </div>
      </section>

      {/* ───── FOOTER (Home.js style) ───── */}
      <footer className="bg-gray-900 bg-opacity-90 backdrop-blur-lg border-t border-gray-700 mt-16">
        <div className="max-w-7xl mx-auto px-4 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="col-span-1 md:col-span-2">
              <Link to="/" className="flex items-center space-x-2 mb-4 group">
                <div className="logo-container bg-gradient-to-r from-blue-500 to-purple-600 bg-clip-text text-transparent group-hover:from-blue-400 group-hover:to-purple-500 transition-all">
                  <span className="text-3xl font-bold">SE</span>
                  <span className="text-3xl font-bold">RRA</span>
                  <span className="text-3xl font-bold">ND</span>
                </div>
              </Link>
              <p className="text-gray-300 text-lg mb-4 leading-relaxed">
                Your trusted platform for connecting with local service providers across Kenya.
              </p>
              <AnimatedSocialLinks />
            </div>

            <div>
              <h3 className="text-white font-bold text-lg mb-4">Quick Links</h3>
              <ul className="space-y-2">
                <li><Link to="/" className="text-gray-300 hover:text-white transition-colors hover:underline">Home</Link></li>
                <li><Link to="/profile" className="text-gray-300 hover:text-white transition-colors hover:underline">Profile</Link></li>
                <li><Link to="/contact" className="text-gray-300 hover:text-white transition-colors hover:underline">Contact</Link></li>
                <li><Link to="/search" className="text-gray-300 hover:text-white transition-colors hover:underline">Search Providers</Link></li>
              </ul>
            </div>

            <div>
              <h3 className="text-white font-bold text-lg mb-4">Legal & Support</h3>
              <ul className="space-y-2">
                <li><Link to="/terms" className="text-gray-300 hover:text-white transition-colors hover:underline">Terms of Service</Link></li>
                <li><Link to="/privacy" className="text-gray-300 hover:text-white transition-colors hover:underline">Privacy Policy</Link></li>
                <li><Link to="/about" className="text-gray-300 hover:text-white transition-colors hover:underline">About Us</Link></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-700 pt-8 mt-8 text-center">
            <p className="text-gray-400">© 2025 SErraND. All rights reserved. Made with love in Kenya</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
